import discoverProvinceApi from 'api/discoverProvince';
import AddImages from 'components/AddImages';
import FormField from 'components/AddLocation/FormField';
import Button from 'components/App/Button';
import InputValidation from 'components/App/InputValidation';
import SelectValidation from 'components/App/SelectValidation';
import { useFormik } from 'formik';
import { Pencil } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Card, Col, FormFeedback, Input, Row,
} from 'reactstrap';
import slugify from 'slugify';
import { actionGetDiscoveryCategories as actionGetDiscoveryCategoriesAction } from 'store/actions';
import { setFormErrorsOrHandleError } from 'utils';
import * as Yup from 'yup';

function SlugInput(props) {
  const { children, defaultValue } = props;
  const [isDisabled, setIsDisabled] = useState(defaultValue);
  return children({ isDisabled, setIsDisabled });
}

function DiscoverProvinceDetails({
  isCreate,
  provinceId,
  provinceDetails,
  callback,

  //
  categoriesLoading,
  categories,
  actionGetDiscoveryCategories,
}) {
  const details = useMemo(() => (isCreate ? {} : provinceDetails), [
    isCreate,
    provinceDetails,
  ]);

  const initialFormValues = useMemo(() => {
    const imagesFromDetails = details?._id
      ? [
        details?.previewImage,
        ...(details?.images || []),
      ].map((image, index) => ({
        id: new Date().getTime() + index,
        image,
        checked: true,
      }))
      : [];

    const categoryIds = (details?.discoverCategories || []).map((c) => (
      typeof c === 'string' ? c : c._id
    ));

    return {
      name: details?.name || '',
      nameEN: details?.nameEN || '',
      description: details?.description || '',
      descriptionEN: details?.descriptionEN || '',
      slug: details?.slug || '',
      isPublished: details?.isPublished || false,
      discoverCategories: categoryIds,
      images: imagesFromDetails,
    };
  }, [details]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,

    validationSchema: Yup.object({
      slug: Yup
        .string()
        .required('Vui lòng nhập slug')
        .max(100, 'Slug không quá 100 ký tự'),
      name: Yup
        .string()
        .required('Vui lòng nhập tên điểm đến')
        .min(1, 'Vui lòng nhập tên điểm đến')
        .max(100, 'Tên điểm đến không quá 100 ký tự'),
      nameEN: Yup
        .string()
        .required('Vui lòng nhập tên điểm đến')
        .min(1, 'Vui lòng nhập tên điểm đến')
        .max(100, 'Tên điểm đến không quá 100 ký tự'),
      description: Yup
        .string()
        .required('Vui lòng nhập mô tả')
        .min(1, 'Vui lòng nhập mô tả')
        .max(2000, 'Mô tả không quá 2000 ký tự'),
      descriptionEN: Yup
        .string()
        .required('Vui lòng nhập mô tả')
        .min(1, 'Vui lòng nhập mô tả')
        .max(2000, 'Mô tả không quá 2000 ký tự'),
      images: Yup
        .array()
        .max(50, 'Vui lòng chọn tối đa 50 ảnh')
        .test(
          'images',
          'Vui lòng chọn ảnh',
          (value) => value.filter((item) => item.checked).length > 0,
        ),
      discoverCategories: Yup
        .array()
        .required('Vui lòng chọn hạng mục')
        .min(1, 'Vui lòng chọn hạng mục'),
    }),

    onSubmit: async (values) => {
      const checkedImages = values.images
        .filter((item) => item.checked)
        .map((item) => item.image);

      try {
        if (isCreate) {
          await discoverProvinceApi.createDiscoverProvince({
            ...values,
            discoverCategories: values.discoverCategories,
            previewImage: checkedImages[0],
            images: checkedImages.slice(1),
          });
        } else {
          await discoverProvinceApi.updateDiscoverProvince(provinceId, {
            ...values,
            discoverCategories: values.discoverCategories,
            previewImage: checkedImages[0],
            images: checkedImages.slice(1),
          });
        }

        if (isCreate) {
          await callback?.();
        } else {
          toast.success('Cập nhật thành công');
          await callback?.();
        }
      } catch (error) {
        setFormErrorsOrHandleError(error, validation);
      }
    },
  });

  const formattedCategories = React.useMemo(() => (categories?.data ?? [])
    .filter((category) => category.isPublished)
    .map((category) => ({
      ...category,
      label: category.name,
      value: category._id,
    })).sort((a, b) => a.label.localeCompare(b.label)), [categories?.data]);

  React.useEffect(() => {
    actionGetDiscoveryCategories({
      isPublished: true,
      page: 1,
      limit: 1000,
    });
  }, []);

  React.useEffect(() => {
    // slug is auto generate from name
    validation.setFieldValue(
      'slug',
      slugify(validation.values.name, '-').toLowerCase(),
      true,
    );
  }, [validation.values.name]);

  return (
    <Row className="justify-content-center">
      <Col lg={12}>
        <Card className="p-3">
          <form onSubmit={validation.handleSubmit}>
            <FormField label="Tên điểm đến - VI" isRequired>
              <InputValidation
                isCustomOnChange
                name="name"
                placeholder="Nhập tên"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={100}
                isShowCounter
              />
            </FormField>

            <FormField label="Tên điểm đến - EN" isRequired>
              <InputValidation
                name="nameEN"
                placeholder="Nhập tên"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={100}
                isShowCounter
              />
            </FormField>

            <FormField label="Mô tả điểm đến - VI" isRequired>
              <InputValidation
                type="textarea"
                name="description"
                placeholder="Nhập mô tả"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={2000}
                style={{ minHeight: '84px' }}
                isShowCounter
              />
            </FormField>

            <FormField label="Mô tả điểm đến - EN" isRequired>
              <InputValidation
                type="textarea"
                name="descriptionEN"
                placeholder="Nhập mô tả"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={2000}
                style={{ minHeight: '84px' }}
                isShowCounter
              />
            </FormField>

            <FormField label="Slug" isRequired>
              <SlugInput defaultValue>
                {({ isDisabled, setIsDisabled }) => (
                  <div className="position-relative">
                    <InputValidation
                      name="slug"
                      type="text"
                      placeholder="Nhập slug"
                      validation={validation}
                      isCustomOnChange
                      onChange={(e) => {
                        validation.setFieldValue(
                          'slug',
                          slugify(e.target.value, '-').toLowerCase(),
                        );
                      }}
                      isDisabled={isDisabled}
                      style={{ paddingRight: '30px' }}
                    />

                    <button
                      type="button"
                      onClick={() => setIsDisabled((prev) => !prev)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px 10px',
                      }}
                    >
                      <Pencil width={16} height={16} />
                    </button>
                  </div>
                )}
              </SlugInput>
            </FormField>

            <FormField label="Hạng mục" isRequired>
              <SelectValidation
                isMulti
                name="discoverCategories"
                placeholder="Chọn hạng mục"
                options={formattedCategories}
                validation={validation}
                value={formattedCategories
                  .filter((category) => validation
                    .values
                    .discoverCategories
                    .includes(category.value))
                }
                onChangeMulti={(selected) => {
                  validation.setFieldValue(
                    'discoverCategories',
                    selected.map((category) => category.value),
                    true);
                }}
                isDisabled={categoriesLoading}
              />
            </FormField>

            <div className="mb-4">
              <AddImages
                value={validation.values.images}
                onChange={(images) =>
                  validation.setFieldValue('images', images, true)
                }
              />

              {validation.errors.images && validation.touched.images && (
                <FormFeedback type="invalid" className="d-block font-size-12">
                  {validation.errors.images}
                </FormFeedback>
              )}
            </div>

            <label
              htmlFor="isPublished"
              className="d-flex align-items-center gap-2"
            >
              <Input
                name="isPublished"
                id="isPublished"
                type="checkbox"
                checked={validation.values.isPublished}
                onChange={(e) => validation.setFieldValue('isPublished', e.target.checked)}
              />
              <span>Xuất bản</span>
            </label>

            <Button
              classes="w-100"
              type="submit"
              loading={validation.isSubmitting}
              disabled={validation.isSubmitting}
            >
              {isCreate ? 'Thêm' : 'Cập nhật'}
            </Button>
          </form>
        </Card>
      </Col>
    </Row>
  );
}

DiscoverProvinceDetails.propTypes = {
  provinceDetails: PropTypes.instanceOf(Object),
  provinceId: PropTypes.string,
  isCreate: PropTypes.bool.isRequired,
  callback: PropTypes.func,
  //
  categoriesLoading: PropTypes.bool.isRequired,
  categories: PropTypes.instanceOf(Array).isRequired,
  actionGetDiscoveryCategories: PropTypes.func.isRequired,
};

DiscoverProvinceDetails.defaultProps = {
  provinceDetails: {},
  callback: () => { },
  provinceId: '',
};

const mapStateToProps = (state) => ({
  categoriesLoading: state.DiscoverCategory.categoriesLoading,
  categories: state.DiscoverCategory.categories,
});

const mapDispatchToProps = ({
  actionGetDiscoveryCategories: actionGetDiscoveryCategoriesAction,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DiscoverProvinceDetails);
